import { useFormatting } from "@msys/ui";
import { clamp } from "lodash-es";
import React from "react";
import { FormattedField } from "./FormattedField.js";

export const FormattedFloatField = ({
  name,
  unit,
  min,
  max,
  step,
  multiplier = 1,
  defaultValue,
  prepareActualValue,
  ...props
}: {
  name: string;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  multiplier?: number;
  defaultValue?: number;
  prepareActualValue?: (value: number) => number;
} & Omit<
  React.ComponentProps<typeof FormattedField<number>>,
  "getFormattedValue" | "getActualValue" | "getEditableValue"
>) => {
  const { getFormattedFloat, getEditableFloat, getFloat } = useFormatting();

  function getFormattedValue(value: number | null | undefined) {
    return getFormattedUnit(
      getFormattedFloat((value ?? 0) * multiplier, step),
      unit
    );
  }

  function getActualValue(value: string) {
    let floatValue =
      (value === "" && defaultValue !== undefined
        ? defaultValue
        : getFloat(value, false)) / multiplier;
    if (step) floatValue = Math.round(floatValue / step) * step;
    floatValue = Number.isNaN(floatValue) ? (defaultValue ?? 0) : floatValue;

    const newValue = prepareActualValue
      ? prepareActualValue(
          clamp(floatValue, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER)
        )
      : clamp(
          floatValue,
          min ?? Number.MIN_SAFE_INTEGER,
          max ?? Number.MAX_SAFE_INTEGER
        );
    return newValue;
  }

  function getEditableValue(value: number | null | undefined) {
    return getEditableFloat((value ?? 0) * multiplier, false, step);
  }

  return (
    <FormattedField
      {...props}
      name={name}
      getFormattedValue={getFormattedValue}
      getActualValue={getActualValue}
      getEditableValue={getEditableValue}
    />
  );
};

function getFormattedUnit(value: string, unit?: string) {
  return `${value}${unit ?? ""}`.trim();
}
